import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { getInterviewDetail } from '../../utils/api';
import ReportLoading from '../../components/ui/ReportLoading';

const LONG_WAIT_MS = 60 * 1000;
const FAST_POLL_MS = 2 * 1000;
const SLOW_POLL_MS = 30 * 1000;
const REPORT_PENDING_MESSAGE = 'Your detailed interview report is being prepared. This is taking longer than usual, but your interview has been saved safely. You can leave this page and come back later; the report will appear automatically once ready.';

const GeneratingResult = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [status, setStatus] = useState('Initializing...');
    const [error, setError] = useState(null);
    const [takingLong, setTakingLong] = useState(false);
    const [pendingMessage, setPendingMessage] = useState(REPORT_PENDING_MESSAGE);

    useEffect(() => {
        // Business hiring candidates must NEVER see a score or report. If this
        // session was launched from a candidate invite, divert straight to the
        // branded thank-you instead of polling the (recruiter-only, server-fenced)
        // report endpoint. Both the manual "End interview" and the auto-timeout
        // paths route through this page, so this single guard covers both. The
        // marker is set by the candidate invite page (business/pages/CandidateInvite)
        // at launch and outlives the interview panel clearing its own session key.
        try {
            const raw = localStorage.getItem('prepxai_biz_candidate');
            if (raw) {
                localStorage.removeItem('prepxai_biz_candidate');
                let info = {};
                try { info = JSON.parse(raw) || {}; } catch (e) { info = {}; }
                navigate('/business/interview-complete', { replace: true, state: info });
                return;
            }
        } catch (e) {
            // Storage unavailable — fall through to the normal consumer flow.
        }

        let cancelled = false;
        const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

        const processResult = async () => {
            const params = new URLSearchParams(location.search);
            const sessionId = location.state?.sessionId || params.get('session') || params.get('id');

            if (!sessionId) {
                setError('No session ID found.');
                return;
            }

            setStatus('Analyzing performance metrics...');
            const startedAt = Date.now();

            while (!cancelled) {
                const elapsed = Date.now() - startedAt;
                const isTakingLong = elapsed >= LONG_WAIT_MS;

                if (isTakingLong) {
                    setTakingLong(true);
                    setStatus(REPORT_PENDING_MESSAGE);
                } else {
                    setStatus('Generating comprehensive report...');
                }

                try {
                    const reportData = await getInterviewDetail(sessionId);

                    if ((reportData?.is_challenge && reportData?.include_report && !reportData?.results_released) || reportData?.report_status === 'results_pending') {
                        setStatus('Submission recorded! Redirecting...');
                        await wait(500);

                        if (!cancelled) {
                            navigate(`/interview-report?session=${sessionId}`, {
                                state: {
                                    sessionId,
                                    report: reportData,
                                }
                            });
                        }
                        return;
                    }

                    if (reportData?.overallData) {
                        setStatus('Report ready! Redirecting...');
                        await wait(500);

                        if (!cancelled) {
                            navigate(`/interview-report?session=${sessionId}`, {
                                state: {
                                    sessionId,
                                    report: reportData,
                                }
                            });
                        }
                        return;
                    }

                    if (reportData?.__httpStatus === 202 || reportData?.report_status) {
                        setPendingMessage(reportData?.message || REPORT_PENDING_MESSAGE);
                    }
                } catch (fetchErr) {
                    const statusCode = fetchErr.response?.status;
                    if (statusCode && statusCode !== 404) {
                        console.log('Report fetch error:', fetchErr);
                    }
                }

                await wait(Date.now() - startedAt >= LONG_WAIT_MS ? SLOW_POLL_MS : FAST_POLL_MS);
            }
        };

        processResult();

        return () => {
            cancelled = true;
        };
    }, [location.search, location.state, navigate]);


    if (!error) {
        return <ReportLoading takingLong={takingLong} message={pendingMessage} status={status} />;
    }

    return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-rose-50 flex items-center justify-center mb-6">
                <Icon name="AlertCircle" size={32} className="text-rose-500" />
            </div>
            <h1 className="text-2xl font-black text-slate-900 mb-2 tracking-tight">Report Not Found</h1>
            <p className="text-slate-500 mb-8 max-w-md">{error}</p>
            <button
                onClick={() => navigate('/dashboard')}
                className="px-8 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all active:scale-95"
            >
                Return to Dashboard
            </button>
        </div>
    );
};

export default GeneratingResult;
